import React, { useRef, useMemo, useCallback } from "react";
import { Line, Bar } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import { Chart as ChartJS, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "./InstrumentCharts.css";
import { createCrosshairPlugin } from "../../shared/util/createCrosshairPlugin";
import {
  buildSignalMarkers,
  chartOptions,
  signalMarkersPlugin,
  toPoints,
} from "../../shared/util/instrumentCharts";

ChartJS.register(...registerables, zoomPlugin, annotationPlugin);

// ─── Component ────────────────────────────────────────────────────────────────
// data: the response of /api/analytics/:ticker, one value per day of
// data.dates. rangeValues: [first, last] day index shown.
const InstrumentCharts = ({ instrument, data, rangeValues }) => {
  const {
    dates, Close, VIX, RSI, MACD_Histogram, NOTIFICATION,
    Volume, BOL_MA, BOL_LOWER, BOL_UPPER, STOP_LOSS_PRICE,
    currency, // USD, or the quote currency where control has no USD rate
  } = data;

  // Chart refs
  const priceChartRef = useRef(null);
  const vixChartRef = useRef(null);
  const rsivixChartRef = useRef(null);
  const volumeChartRef = useRef(null);
  const macdChartRef = useRef(null);
  const rsiChartRef = useRef(null);

  const allChartRefs = useMemo(
    () => [priceChartRef, vixChartRef, rsivixChartRef, volumeChartRef, macdChartRef, rsiChartRef],
    []
  );

  // Crosshair
  const cursorPositionRef = useRef({ index: null, y: null, currentChartId: null });
  const crosshairPlugin = useMemo(() => createCrosshairPlugin(cursorPositionRef), []);

  // Mouse handlers
  const handleChartMouseMove = useCallback((event, chart) => {
    const rect = chart.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const index = Math.round(chart.scales.x.getValueForPixel(x));
    const y = event.clientY - rect.top;

    cursorPositionRef.current.index = index;
    cursorPositionRef.current.y = y;
    cursorPositionRef.current.currentChartId = chart.id;

    allChartRefs.forEach((ref) => { if (ref.current) ref.current.draw(); });
  }, [allChartRefs]);

  const handleChartMouseOut = useCallback(() => {
    cursorPositionRef.current.index = null;
    cursorPositionRef.current.y = null;
    cursorPositionRef.current.currentChartId = null;

    allChartRefs.forEach((ref) => { if (ref.current) ref.current.draw(); });
  }, [allChartRefs]);

  // ─── Memoized chart data ───────────────────────────────────────────────────
  const priceData = useMemo(() => ({
    datasets: [
      {
        label: "Closing Prices",
        data: toPoints(Close),
        fill: true,
        backgroundColor: "rgba(75,192,192,0.2)",
        borderColor: "rgba(50,128,128,1)",
        borderWidth: 0.8, pointRadius: 0,
      },
      {
        label: "BOL MA",
        data: toPoints(BOL_MA),
        fill: false, borderColor: "rgba(0,0,255,.6)",
        borderWidth: 0.8, pointRadius: 0, yAxisID: "y",
      },
      {
        label: "BOL UP",
        data: toPoints(BOL_UPPER),
        fill: false, borderColor: "rgba(0,0,0,.6)",
        borderWidth: 0.8, pointRadius: 0, yAxisID: "y",
      },
      {
        label: "BOL LOW",
        data: toPoints(BOL_LOWER),
        fill: false, borderColor: "rgba(0,0,0,.6)",
        borderWidth: 0.8, pointRadius: 0, yAxisID: "y",
      },
      {
        label: "SL",
        data: toPoints(STOP_LOSS_PRICE.map((v) => (v !== 0 ? v : null))),
        fill: false, borderColor: "rgba(255,0,0,1)",
        borderWidth: 0.8, pointRadius: 0, yAxisID: "y",
      },
    ],
  }), [Close, BOL_MA, BOL_UPPER, BOL_LOWER, STOP_LOSS_PRICE]);

  const vixData = useMemo(() => ({
    datasets: [{
      label: "VIX", data: toPoints(VIX), type: "line",
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(75,192,192,1)", borderWidth: 1.5, pointRadius: 0, yAxisID: "y",
    }],
  }), [VIX]);

  // RSI standardized by VIX, a simple ratio
  const rsivixData = useMemo(() => ({
    datasets: [{
      label: "RSI_VIX",
      data: toPoints(RSI.map((rsi, i) => (rsi != null && VIX[i] ? rsi / VIX[i] : null))),
      type: "line",
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(75,192,192,1)", borderWidth: 1.5, pointRadius: 0, yAxisID: "y",
    }],
  }), [RSI, VIX]);

  const volumeData = useMemo(() => ({
    datasets: [{
      label: "Volume", data: toPoints(Volume.map((v) => (v != null ? v / 1e6 : null))), type: "bar",
      backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(75,192,192,1)", borderWidth: 1,
    }],
  }), [Volume]);

  const rsiData = useMemo(() => ({
    datasets: [{
      label: "RSI", data: toPoints(RSI),
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(75,192,192,.8)", borderWidth: 1.5, pointRadius: 0,
    }],
  }), [RSI]);

  const macdData = useMemo(() => ({
    datasets: [{
      label: "MACD Histogram",
      data: toPoints(MACD_Histogram),
      type: "bar",
      backgroundColor: MACD_Histogram.map((v) =>
        v >= 0 ? "rgba(0, 255, 0, 0.5)" : "rgba(255, 0, 0, 0.5)"
      ),
      borderColor: MACD_Histogram.map((v) =>
        v >= 0 ? "rgba(0, 255, 0, 1)" : "rgba(255, 0, 0, 1)"
      ),
      borderWidth: 1,
    }],
  }), [MACD_Histogram]);

  // ─── Memoized signals (computed once per data change) ──────────────────────
  const signals = useMemo(() => buildSignalMarkers(NOTIFICATION), [NOTIFICATION]);

  // ─── Memoized chart options ────────────────────────────────────────────────
  const priceOptions = useMemo(
    () => chartOptions({ title: `Price [${currency || "USD"}]`, range: rangeValues, dates, signals }),
    [rangeValues, dates, signals, currency]
  );

  const macdOptions = useMemo(
    () => chartOptions({ title: "MACD [-]", range: rangeValues, dates, signals }),
    [rangeValues, dates, signals]
  );

  const rsivixOptions = useMemo(
    () => chartOptions({ title: "RSI/VIX [-]", range: rangeValues, dates, signals }),
    [rangeValues, dates, signals]
  );

  const rsiOptions = useMemo(() => chartOptions({
    title: "RSI [-]", range: rangeValues, dates, signals,
    y: { min: 0, max: 100 },
    annotations: {
      line1: {
        type: "line",
        yMin: instrument.triggerThresholds.RSI_BUY,
        yMax: instrument.triggerThresholds.RSI_BUY,
        borderColor: "red", borderWidth: 1.5,
      },
      line2: {
        type: "line",
        yMin: instrument.triggerThresholds.RSI_SELL,
        yMax: instrument.triggerThresholds.RSI_SELL,
        borderColor: "red", borderWidth: 1.5,
      },
    },
  }), [rangeValues, dates, signals, instrument.triggerThresholds.RSI_BUY, instrument.triggerThresholds.RSI_SELL]);

  const vixOptions = useMemo(
    () => chartOptions({ title: "VIX [-]", range: rangeValues, dates, signals }),
    [rangeValues, dates, signals]
  );

  const volumeOptions = useMemo(
    () => chartOptions({ title: "Volume [M]", range: rangeValues, dates, signals, showDates: true }),
    [rangeValues, dates, signals]
  );

  // ─── Stable plugin array ───────────────────────────────────────────────────
  const plugins = useMemo(() => [signalMarkersPlugin, crosshairPlugin], [crosshairPlugin]);

  return (
    <div>
      <div className="chart-container">
        <div className="chart-wrapper">
          <div className="price-chart">
            <Line
              ref={priceChartRef}
              data={priceData}
              options={priceOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, priceChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="macd-chart">
            <Bar
              ref={macdChartRef}
              data={macdData}
              options={macdOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, macdChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="vix-chart">
            <Line
              ref={rsivixChartRef}
              data={rsivixData}
              options={rsivixOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, rsivixChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="rsi-chart">
            <Line
              ref={rsiChartRef}
              data={rsiData}
              options={rsiOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, rsiChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="vix-chart">
            <Line
              ref={vixChartRef}
              data={vixData}
              options={vixOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, vixChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="volume-chart">
            <Bar
              ref={volumeChartRef}
              data={volumeData}
              options={volumeOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, volumeChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstrumentCharts;
